import { useEffect, useRef } from "react";
import { useGame, MODES } from "./store";

// Simulates ArrowUp / ArrowDown keyboard events from touch buttons.
// GameLoop already listens for these key events.

function simulateKey(code, pressed) {
  const type = pressed ? "keydown" : "keyup";
  window.dispatchEvent(new KeyboardEvent(type, { code, bubbles: true }));
}

export default function TouchControls() {
  const mode = useGame((s) => s.mode);
  const isMobile = "ontouchstart" in window || window.innerWidth < 768;

  const accelRef = useRef(false);
  const brakeRef = useRef(false);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (accelRef.current) simulateKey("ArrowUp", false);
      if (brakeRef.current) simulateKey("ArrowDown", false);
    };
  }, []);

  if (!isMobile || mode !== MODES.ON_TRACK) return null;

  const btnStyle = (color) => ({
    width: 90,
    height: 90,
    borderRadius: "50%",
    border: `3px solid ${color}`,
    background: `radial-gradient(circle at 40% 35%, ${color}33 0%, rgba(0,0,0,0.7) 100%)`,
    color: "#fff",
    fontSize: 32,
    fontWeight: 900,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    userSelect: "none",
    WebkitUserSelect: "none",
    touchAction: "none",
    backdropFilter: "blur(8px)",
    boxShadow: `0 0 24px ${color}55`,
    transition: "transform 0.08s",
    gap: 2,
  });

  const labelStyle = {
    fontSize: 9,
    letterSpacing: 2,
    fontWeight: 700,
    marginTop: -2,
    color: "rgba(255,255,255,0.75)",
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: 32,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 32px",
        zIndex: 30,
        pointerEvents: "none",
      }}
    >
      {/* BRAKE button — left */}
      <div
        style={{ ...btnStyle("#ff4d6d"), pointerEvents: "auto" }}
        onTouchStart={(e) => {
          e.preventDefault();
          brakeRef.current = true;
          simulateKey("ArrowDown", true);
          e.currentTarget.style.transform = "scale(0.92)";
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          brakeRef.current = false;
          simulateKey("ArrowDown", false);
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <span>⬇</span>
        <span style={labelStyle}>BRAKE</span>
      </div>

      {/* Speed indicator in the middle */}
      <SpeedBadge />

      {/* ACCELERATE button — right */}
      <div
        style={{ ...btnStyle("#00ffaa"), pointerEvents: "auto" }}
        onTouchStart={(e) => {
          e.preventDefault();
          accelRef.current = true;
          simulateKey("ArrowUp", true);
          e.currentTarget.style.transform = "scale(0.92)";
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          accelRef.current = false;
          simulateKey("ArrowUp", false);
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <span>⬆</span>
        <span style={labelStyle}>ACCEL</span>
      </div>
    </div>
  );
}

function SpeedBadge() {
  const v = useGame((s) => s.v);
  const danger = v > 35;
  return (
    <div
      style={{
        textAlign: "center",
        padding: "8px 18px",
        background: "rgba(10,12,24,0.8)",
        border: `1.5px solid ${danger ? "#ff4d6d" : "rgba(255,255,255,0.2)"}`,
        borderRadius: 14,
        backdropFilter: "blur(8px)",
        animation: danger ? "pulse 0.5s ease-in-out infinite alternate" : "none",
      }}
    >
      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          color: danger ? "#ff4d6d" : "#00ffaa",
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1,
        }}
      >
        {v.toFixed(0)}
      </div>
      <div style={{ fontSize: 9, color: "#9aa4b2", letterSpacing: 2, marginTop: 2 }}>
        {danger ? "⚠ FAST!" : "m/s"}
      </div>
      <style>{`@keyframes pulse { from { box-shadow: 0 0 0 0 #ff4d6d55; } to { box-shadow: 0 0 16px 4px #ff4d6d99; } }`}</style>
    </div>
  );
}
